<template>
  <div class="tool-page">
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('wordFreq.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('wordFreq.title') }}</h1>
      <p>{{ $t('wordFreq.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="options">
        <label class="option-label">
          <input type="checkbox" v-model="ignoreCase" />
          {{ $t('wordFreq.ignoreCase') }}
        </label>
        <label class="option-label">
          <input type="checkbox" v-model="ignorePunctuation" />
          {{ $t('wordFreq.ignorePunctuation') }}
        </label>
        <label class="option-label">
          <input type="checkbox" v-model="skipSingleChinese" />
          {{ $t('wordFreq.skipSingleChinese') }}
        </label>
        <label class="option-label">
          <input type="checkbox" v-model="skipPureNumbers" />
          {{ $t('wordFreq.skipPureNumbers') }}
        </label>
        <label class="option-label">
          <input type="checkbox" v-model="skipEnglishPrepositions" />
          {{ $t('wordFreq.skipEnglishPrepositions') }}
        </label>
        <div class="option-group-inline">
          <label>{{ $t('wordFreq.topN') }}</label>
          <input type="number" v-model.number="topN" min="5" max="500" />
        </div>
      </div>

      <div class="actions">
        <button class="btn primary" @click="analyze">{{ $t('wordFreq.analyze') }}</button>
        <button class="btn" @click="copyResult" :disabled="!results.length">{{ $t('common.copyResult') }}</button>
        <button class="btn" @click="clearAll">{{ $t('common.clear') }}</button>
      </div>

      <div v-if="successMessage" class="alert success">{{ successMessage }}</div>

      <div class="editor-panel">
        <label>{{ $t('wordFreq.inputLabel') }}</label>
        <textarea v-model="input" :placeholder="$t('wordFreq.inputPlaceholder')" spellcheck="false"></textarea>
      </div>

      <div v-if="results.length" class="results-section">
        <div class="results-header">
          <h3>{{ $t('wordFreq.resultTitle') }}
            <span class="stats-info">{{ $t('wordFreq.totalWords').replace('{n}', totalWords) }}</span>
          </h3>
        </div>
        <div class="results-table">
          <div class="table-header">
            <span class="col-rank">#</span>
            <span class="col-word">{{ $t('wordFreq.word') }}</span>
            <span class="col-count">{{ $t('wordFreq.count') }}</span>
            <span class="col-percent">{{ $t('wordFreq.percent') }}</span>
            <span class="col-bar">{{ $t('wordFreq.bar') }}</span>
          </div>
          <div class="table-row" v-for="(item, i) in results" :key="i">
            <span class="col-rank">{{ i + 1 }}</span>
            <span class="col-word">{{ item.word }}</span>
            <span class="col-count">{{ item.count }}</span>
            <span class="col-percent">{{ item.percent }}%</span>
            <span class="col-bar">
              <span class="bar-fill" :style="{ width: item.barWidth + '%' }"></span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="tool-description">
      <h2>{{ $t('wordFreq.whatIs') }}</h2>
      <p>{{ $t('wordFreq.whatIsDesc') }}</p>
      <h2>{{ $t('wordFreq.useCasesTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('wordFreq.useCases')" :key="i">{{ item }}</li>
      </ul>
      <h2>{{ $t('wordFreq.tipsTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('wordFreq.tips')" :key="i">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
import { segmentChinese, ENGLISH_PREPOSITIONS } from '@/utils/chineseSegment'

export default {
  name: 'WordFrequency',
  data() {
    return {
      input: '', results: [], successMessage: '',
      ignoreCase: true, ignorePunctuation: true, topN: 50,
      skipSingleChinese: true, skipPureNumbers: true, skipEnglishPrepositions: true,
      totalWords: 0
    }
  },
  methods: {
    analyze() {
      if (!this.input.trim()) return

      let text = this.input
      if (this.ignorePunctuation) {
        text = text.replace(/[，。！？、；：\u201C\u201D\u2018\u2019（）《》【】…—·\-,.!?;:'"()[\]{}<>@#$%^&*+=~`|/\\]/g, ' ')
      }

      // 使用分词工具进行中文分词 + 英文/数字提取
      const tokens = segmentChinese(text)

      const freq = {}

      tokens.forEach(token => {
        // 判断是否是纯数字
        if (this.skipPureNumbers && /^\d+\.?\d*$/.test(token)) return

        // 判断是否是中文单字
        if (this.skipSingleChinese && /^[\u4e00-\u9fff\u3400-\u4dbf]$/.test(token)) return

        // 判断是否是英文介词/虚词
        if (this.skipEnglishPrepositions && /^[a-zA-Z]+$/.test(token)) {
          const lower = token.toLowerCase()
          if (ENGLISH_PREPOSITIONS.has(lower)) return
        }

        const key = this.ignoreCase ? token.toLowerCase() : token
        freq[key] = (freq[key] || 0) + 1
      })

      const sorted = Object.entries(freq)
        .sort((a, b) => b[1] - a[1])
        .slice(0, this.topN)

      this.totalWords = Object.values(freq).reduce((a, b) => a + b, 0)
      const maxCount = sorted.length > 0 ? sorted[0][1] : 1

      this.results = sorted.map(([word, count]) => ({
        word,
        count,
        percent: ((count / this.totalWords) * 100).toFixed(1),
        barWidth: (count / maxCount) * 100
      }))

      this.successMessage = this.$t('wordFreq.success')
      setTimeout(() => { this.successMessage = '' }, 2000)
    },
    async copyResult() {
      const text = this.results.map(r => `${r.word}:${r.count}`).join('\n')
      try { await navigator.clipboard.writeText(text); this.successMessage = this.$t('common.copied') }
      catch { this.successMessage = this.$t('common.copyFailed') }
      setTimeout(() => { this.successMessage = '' }, 2000)
    },
    clearAll() { this.input = ''; this.results = []; this.successMessage = ''; this.totalWords = 0 }
  }
}
</script>

<style scoped>
.tool-page { max-width: 1200px; margin: 0 auto; }
.breadcrumb { margin-bottom: 24px; font-size: 14px; color: var(--text-secondary); }
.breadcrumb a { color: var(--accent-color); text-decoration: none; }
.breadcrumb a:hover { text-decoration: underline; }
.breadcrumb .separator { margin: 0 8px; color: var(--text-muted); }
.tool-header { text-align: center; margin-bottom: 32px; }
.tool-header h1 { font-size: 32px; margin-bottom: 12px; color: var(--text-primary); }
.tool-header p { color: var(--text-secondary); font-size: 16px; max-width: 600px; margin: 0 auto; line-height: 1.6; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; box-shadow: var(--shadow-md); }
.options { display: flex; gap: 20px; flex-wrap: wrap; margin-bottom: 16px; align-items: center; }
.option-label { display: flex; align-items: center; gap: 6px; color: var(--text-secondary); font-size: 14px; cursor: pointer; }
.option-group-inline { display: flex; align-items: center; gap: 6px; }
.option-group-inline label { font-size: 14px; color: var(--text-secondary); white-space: nowrap; }
.option-group-inline input { width: 60px; padding: 6px 10px; background: var(--bg-input); border: 1px solid var(--border-color); border-radius: 6px; color: var(--text-primary); font-size: 14px; }
.actions { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
.btn { background: var(--bg-tertiary); color: var(--text-primary); border: 1px solid var(--border-color); border-radius: 8px; padding: 10px 20px; cursor: pointer; font-size: 14px; transition: all 0.2s; }
.btn:hover { background: var(--bg-hover); }
.btn.primary { background: var(--accent-color); border-color: var(--accent-color); color: white; }
.btn.primary:hover { background: var(--accent-hover); }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.alert.success { padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.3); color: var(--success-color); }
.editor-panel { display: flex; flex-direction: column; margin-bottom: 20px; }
.editor-panel label { font-size: 14px; color: var(--text-secondary); margin-bottom: 8px; }
.editor-panel textarea { min-height: 200px; background: var(--bg-input); border: 1px solid var(--border-color); border-radius: 8px; padding: 16px; color: var(--text-primary); font-size: 14px; line-height: 1.6; resize: vertical; }
.editor-panel textarea:focus { outline: none; border-color: var(--accent-color); }
.results-section { margin-top: 16px; }
.results-header h3 { font-size: 18px; color: var(--text-primary); margin-bottom: 16px; display: flex; align-items: center; gap: 12px; }
.stats-info { font-size: 13px; color: var(--accent-color); font-weight: normal; }
.results-table { font-size: 14px; }
.table-header { display: flex; padding: 10px 12px; background: var(--bg-tertiary); border-radius: 8px 8px 0 0; font-weight: 600; color: var(--text-primary); }
.table-row { display: flex; padding: 8px 12px; border-bottom: 1px solid var(--border-color); align-items: center; }
.table-row:hover { background: var(--bg-hover); }
.col-rank { width: 40px; color: var(--text-muted); }
.col-word { width: 120px; font-weight: 500; color: var(--text-primary); word-break: break-all; }
.col-count { width: 60px; text-align: center; color: var(--accent-color); font-weight: 600; }
.col-percent { width: 70px; text-align: center; color: var(--text-secondary); }
.col-bar { flex: 1; padding-left: 12px; }
.bar-fill { display: block; height: 16px; background: linear-gradient(90deg, var(--accent-color), var(--accent-light)); border-radius: 4px; transition: width 0.3s; }
.tool-description { margin-top: 32px; background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 32px; box-shadow: var(--shadow-sm); }
.tool-description h2 { font-size: 20px; color: var(--text-primary); margin: 24px 0 16px; padding-bottom: 8px; border-bottom: 2px solid var(--accent-light); }
.tool-description h2:first-child { margin-top: 0; }
.tool-description p { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 16px; }
.tool-description ul { padding-left: 24px; margin-bottom: 16px; }
.tool-description li { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 8px; }
</style>
